import React, { type ReactNode, useState } from "react";

import clsx from "clsx";
import { format, parseISO } from "date-fns";

import { useUser } from "@components/user-context";
import { useFeatureFlags } from "@hooks/http";
import { type Documents } from "@north-beam/nb-client-side";
import { IconButton } from "@shared/buttons/icon-button";

import { DeleteDocumentModal } from "../modals";
import { ShareDocumentModal } from "../modals/share-document-modal";

type Doc = Documents["me"]["documents"]["data"][number];

export interface DocumentsTableRowProps {
  readonly document: Doc;
  readonly children: ReactNode;
  readonly onDeleteSuccess: () => void;
}

export const DocumentsTableRow = ({
  document,
  children,
  onDeleteSuccess,
}: DocumentsTableRowProps) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { hasFeatureFlag } = useFeatureFlags();

  return (
    <>
      <tr key={document.id}>
        <td className="align-middle">
          <a
            className="whitespace-nowrap hover:text-cosmic+1"
            href={document.link.authenticatedURL}
            target="_blank"
            rel="noreferrer"
          >
            {document.name}
          </a>
        </td>
        <td className="align-middle">
          {format(parseISO(document.metadata.updatedAt), "PPPPpppp")}
        </td>
        <td className="align-middle">
          {format(parseISO(document.metadata.createdAt), "PPPPpppp")}
        </td>
        <td className="align-middle">{children}</td>
        <td className="align-middle">
          <div className="flex flex-col items-center justify-center">
            <IconButton
              size="sm"
              leftIcon={<i className="fa-regular fa-sharp fa-trash" />}
              className={clsx("btn-outline-danger w-fit transition-[width]")}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </IconButton>
            {hasFeatureFlag("enableDocumentsSharing") && (
              <ShareDocumentButtonAndModal document={document} />
            )}
          </div>
        </td>
      </tr>
      <DeleteDocumentModal
        isOpen={deleteModalOpen}
        document={document}
        onClose={() => setDeleteModalOpen(false)}
        onSuccess={() => {
          setDeleteModalOpen(false);
          onDeleteSuccess();
        }}
      />
    </>
  );
};

const ShareDocumentButtonAndModal = ({ document }: { document: Doc }) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { user } = useUser();
  return (
    <>
      <IconButton
        size="sm"
        leftIcon={<i className="fa-sharp fa-regular fa-link" />}
        className={clsx(" btn-outline-primary w-fit transition-[width]")}
        onClick={() => setIsShareModalOpen(true)}
      >
        Share
      </IconButton>
      <ShareDocumentModal
        user={user}
        document={document}
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </>
  );
};
